// productFileParser.js
// Turns an uploaded JSON or Excel (.xlsx / .xls) file into plain product records for Image Creation:
//   { name, price, description, imageUrl }
// The file is only ever READ AS DATA in the browser: JSON.parse for .json, the SheetJS reader (formulas off, first sheet
// only, row-capped) for Excel. Nothing in a file is evaluated or executed, and the file itself is never sent to the
// server — only the resulting records are, and the server validates them again (POST /product-records/validate).

export const PRODUCT_FILE_EXTENSIONS = [".json", ".xlsx", ".xls"];
export const PRODUCT_FILE_ACCEPT = PRODUCT_FILE_EXTENSIONS.join(",");
export const PRODUCT_FILE_MAX_BYTES = 5 * 1024 * 1024;
// Keep in step with Backend/services/productRecordValidation.js (LIMITS.maxRecords, IMAGE_BATCH_MAX_RECORDS).
export const PRODUCT_MAX_RECORDS = 25;

const ALIASES = {
  name: ["name", "title", "item", "itemname", "product", "productname", "dish"],
  price: ["price", "cost", "amount", "rate"],
  description: ["description", "desc", "details", "about"],
  imageUrl: ["imageurl", "image", "imagelink", "photo", "photourl", "picture", "img", "imageurl1"],
};

const normKey = (k) => String(k ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const toText = (v) => {
  if (v === undefined || v === null) return "";
  if (typeof v === "object") return ""; // nested objects/arrays are not product fields
  return String(v).trim();
};

/** Maps a row (any header spelling) to a record, or null when the row is completely empty. */
export function recordFromRow(row) {
  if (row === null || typeof row !== "object" || Array.isArray(row)) return undefined; // not a row at all
  const byKey = new Map();
  for (const [k, v] of Object.entries(row)) byKey.set(normKey(k), v);
  const pick = (field) => {
    for (const alias of ALIASES[field]) if (byKey.has(alias)) return toText(byKey.get(alias));
    return "";
  };
  const record = { name: pick("name"), price: pick("price"), description: pick("description"), imageUrl: pick("imageUrl") };
  return Object.values(record).every((v) => v === "") ? null : record;
}

const hasNameColumn = (rows) =>
  rows.some((r) => r && typeof r === "object" && !Array.isArray(r) && Object.keys(r).some((k) => ALIASES.name.includes(normKey(k))));

/** Pure: array of row objects → records. Throws Error(message) for whole-file problems. */
export function recordsFromRows(rows) {
  if (!Array.isArray(rows)) throw new Error("The file must contain a list of products.");
  const nonEmpty = rows.filter((r) => r !== null && r !== undefined);
  if (nonEmpty.length === 0) throw new Error("The file has no products.");
  if (nonEmpty.length > PRODUCT_MAX_RECORDS) {
    throw new Error(`Too many products: ${nonEmpty.length}. The limit is ${PRODUCT_MAX_RECORDS} per upload.`);
  }
  if (!hasNameColumn(nonEmpty)) throw new Error("Couldn't find a name column. Use the headings name, price and description.");
  const records = [];
  nonEmpty.forEach((row) => {
    const rec = recordFromRow(row);
    if (rec === undefined) records.push({ name: "", price: "", description: "", imageUrl: "" }); // surfaces as an invalid row
    else if (rec) records.push(rec);
  });
  if (records.length === 0) throw new Error("The file has no products.");
  return records;
}

export function checkProductFile(file) {
  if (!file) return "Please choose a file.";
  const name = String(file.name || "").toLowerCase();
  if (!PRODUCT_FILE_EXTENSIONS.some((ext) => name.endsWith(ext))) return "Please upload a .json, .xlsx or .xls file.";
  if (file.size === 0) return "That file is empty.";
  if (file.size > PRODUCT_FILE_MAX_BYTES) return `The file must be ${PRODUCT_FILE_MAX_BYTES / (1024 * 1024)} MB or smaller.`;
  return "";
}

const readFile = (file, as) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Couldn't read the file."));
    if (as === "text") reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
  });

async function parseJson(file) {
  let data;
  try {
    data = JSON.parse(await readFile(file, "text")); // data only; never evaluated
  } catch (err) {
    if (err.message === "Couldn't read the file.") throw err;
    throw new Error("That isn't valid JSON. Check the file and try again.");
  }
  // Accept a bare list, or an object wrapping the list under products / items.
  const rows = Array.isArray(data) ? data : data && typeof data === "object" ? data.products || data.items : null;
  return recordsFromRows(rows);
}

async function parseExcel(file) {
  const XLSX = await import("xlsx");
  let rows;
  try {
    const wb = XLSX.read(new Uint8Array(await readFile(file, "buffer")), {
      type: "array",
      cellFormula: false,
      cellHTML: false,
      cellStyles: false,
      bookVBA: false,
      sheetRows: PRODUCT_MAX_RECORDS + 2, // header + one row past the limit, enough to detect "too many"
    });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    if (!sheet) throw new Error("empty");
    rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
  } catch (err) {
    throw new Error("Couldn't read that spreadsheet. Save it as .xlsx and try again.");
  }
  return recordsFromRows(rows);
}

/** @returns {Promise<Array<{name, price, description, imageUrl}>>}; rejects with Error(message) a person can read. */
export async function parseProductFile(file) {
  const problem = checkProductFile(file);
  if (problem) throw new Error(problem);
  return String(file.name).toLowerCase().endsWith(".json") ? parseJson(file) : parseExcel(file);
}
